import { useCallback, useEffect, useRef, useState } from 'react';

/**
 *
 * @param channelName The name of the broadcast channel
 * @param onMessageReceived callback
 */
export const useBroadcastChannel = <MessageType>(
  channelName: string,
  onMessageReceived: (message: MessageType, broadcastChannel: BroadcastChannel) => void,
) => {
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const broadcastChannel = broadcastChannelRef.current;
  const [isOpen, setIsOpen] = useState(false);

  const callbackRef =
    useRef<(message: MessageType, broadcastChannel: BroadcastChannel) => void | null>();
  callbackRef.current = onMessageReceived;

  const emitMessage = useCallback(
    (message: MessageType) => {
      broadcastChannel?.postMessage(message);
    },
    [broadcastChannel],
  );

  const closeBroadcastChannel = () => {
    broadcastChannel?.close();
    setIsOpen(false);
  };

  useEffect(() => {
    try {
      broadcastChannelRef.current = new window.BroadcastChannel(channelName);
      broadcastChannelRef.current.onmessage = (event) => {
        const broadcastChannel = broadcastChannelRef.current as BroadcastChannel;
        callbackRef.current?.(event.data, broadcastChannel);
      };
      setIsOpen(true);
      return () => {
        broadcastChannelRef.current?.close();
        setIsOpen(false);
      };
    } catch (e) {
      console.error('Broadcast channel not supported');
    }
  }, [channelName]);

  return { broadcastChannel, isOpen, closeBroadcastChannel, emitMessage };
};
